import type { ApiAgent } from '../api/ApiAgent';
import type { Agent } from '@/core/entities/agent/Agent';
import type {
	FeatureObject,
	PropertyTypeObject,
	PropertyTypeSlug
} from '@/core/entities/CRMEnums';
import type { SystemEnums } from '@/core/entities/SystemEnums';
import type {
	ApiCluster,
	ApiProperty,
	ApiPropertyDuplicationGroup,
	ApiPropertyImage,
	ApiTransactionType,
	ApiVideoView,
	PropertyPublicationStatusValue
} from '~/core/entities/api/ApiProperty';
import type { H3ClusterId } from '~~/core/ports/repositories';

import {
	featureCategoriesTranslations,
	FeatureCategory,
	FeatureEnum,
	PropertyTypeEnum,
	propertyTypesTranslations,
	propertyTypesValueBySlug
} from '@/core/entities/CRMEnums';
import { apiTransactionTypeToDomain } from '~/core/entities/api/ApiProperty';
import { extractYoutubeVideoId, snakeToCamelObject } from '~~/utils';

function resolveApiThumbnailUrl(
	thumbnail: ApiProperty['thumbnail']
): string | null {
	if (!thumbnail) return null;
	const url = (thumbnail.thumbnailUrl ?? '').toString().trim();
	return url.length > 0 ? url : null;
}

function normalizeApiYoutubeVideos(
	videos: ApiProperty['videos'] | undefined
): string[] {
	return (videos ?? [])
		.map(video => video?.trim())
		.filter((video): video is string => Boolean(video))
		.filter(video => extractYoutubeVideoId(video) !== null);
}

export interface PropertyImage {
	url?: string;
	watermarkUrl: string;
}

export type TransactionType = 'rent' | 'sell' | 'rentOrSell';

export interface Ad {
	title: string;
	description: string;
	transactionType: TransactionType;
}
export interface Company {
	id: string;
	name: string;
	smallLogo: string | null;
	largeLogo: string | null;
}

export enum DistanceTime {
	OVER_15_MIN = 'OVER_15_MIN',
	UNDER_15_MIN = 'UNDER_15_MIN',
	UNDER_10_MIN = 'UNDER_10_MIN',
	UNDER_5_MIN = 'UNDER_5_MIN'
}

export const DistanceTimeLabels: Record<DistanceTime, string> = {
	[DistanceTime.OVER_15_MIN]: 'Mais de 15 min',
	[DistanceTime.UNDER_15_MIN]: 'Menos de 15 min',
	[DistanceTime.UNDER_10_MIN]: 'Menos de 10 min',
	[DistanceTime.UNDER_5_MIN]: 'Menos de 5 min'
};

export interface ClusterProperties {
	pointCount: number;
	pointIds: string[] | undefined;
	h3Boundary: [number, number][];
}

export interface Cluster extends Omit<ApiCluster, 'properties'> {
	properties: ClusterProperties;
}

export interface VideoViewInfo {
	viewId: string;
	maxCompletionRate: number;
	lastCompletionRate: number;
	totalDuration: number;
	viewCount: number;
	lastViewedAt: string;
}

/**
 * Short video item with processed video metadata
 */
/**
 * One rung of the HLS ladder, resolved when the video was published.
 *
 * Canonical shape for the whole app: the API entity and the warm-up composable
 * both refer to this, so the ladder is described in one place and the
 * dependency points from composables to entities, never the other way.
 */
export interface PrefetchVariant {
	height: number;
	/** Declared BANDWIDTH of the rung, in bits per second. */
	bandwidth: number;
	playlistUrl: string;
	firstSegmentUrl: string;
	/**
	 * CMAF only: init segment the player fetches before the first media
	 * segment. Absent on classic TS ladders.
	 */
	initUrl?: string | null;
}

export interface ShortVideoItem {
	url: string;
	thumbnail: string;
	duration: number;
	width: number;
	height: number;
	quality: string;
	processedAt: string;
	origin: string;
	prefetchVariants?: PrefetchVariant[] | null;
}

/**
 * Converts ApiVideoView to VideoViewInfo.
 * Maps API fields to domain fields with sensible defaults.
 */
function mapApiVideoView(
	apiVideoView: ApiVideoView | undefined
): VideoViewInfo | undefined {
	if (!apiVideoView) return undefined;
	return {
		viewId: apiVideoView.viewId,
		maxCompletionRate: apiVideoView.completionRate,
		lastCompletionRate: apiVideoView.completionRate,
		totalDuration: apiVideoView.duration,
		viewCount: 1,
		lastViewedAt: new Date().toISOString()
	};
}

export interface ShortVideoProperty {
	id: string;
	slug: string;
	commercialId: string;
	ad?: Pick<Ad, 'title'>;
	thumbnailUrl: string | null;
	askingPrice: number | null;
	area: number;
	bedrooms: number | null;
	suites?: number | null;
	parkingSpots: number | null;
	region: string;
	city: string;
	state: string;
	featuredVideos: ShortVideoItem[];
	images: PropertyImage[];
	videoView?: VideoViewInfo;
	watched?: boolean;
	propertyType?: { identifier: string; name: string };
	agent?: {
		id?: string;
		name: string;
		/** `users.display_name` from API; when null/empty, UI uses `name`. */
		displayName?: string | null;
		profilePictureUrl: string | null;
		/** Public profile slug. Not sent by the API yet — see `resolveAgentProfileSlug`. */
		slug?: string | null;
	};
	company?: {
		name: string;
		logoUrl: string | null;
	};
	isExclusive?: boolean;
}

export interface Property {
	id: string;
	agent: Agent;
	company: Company;
	slug: string;
	ad: Ad;
	images: PropertyImage[];
	thumbnailUrl: string | null;
	askingPrice: number | null;
	rentPrice: number | null;
	isExclusive: boolean;
	area: number;
	totalArea: number | null;
	bedrooms: number | null;
	bathrooms: number | null;
	suites: number | null;
	parkingSpots: number | null;
	condoFee: number | null;
	condoName: string | null;
	building: string | null;
	tax: number | null;
	propertyType: PropertyTypeObject;
	publicationStatus: PropertyPublicationStatusValue;
	featuresByCategory: Record<
		(typeof featureCategoriesTranslations)[FeatureCategory],
		FeatureObject[]
	>;
	region: string;
	city: string;
	state: string;
	commercialId: string;
	videos: string[];
	featuredVideos: ShortVideoItem[];
	pluscodeGroup: string;
	launchDate: string;
	propertySummary?: string;
	canonicalCondo?: {
		name: string;
		slug: string;
		constructionYear: number;
	};
	poiDistanceTime?: string | null;
	h3Cluster?: Cluster;
	h3ClusterIds?: H3ClusterId[];
	/**
	 * Optional flag set when `markUserWishlistsId` is used in queries and
	 * the property exists in that user's wishlists.
	 */
	inUserWishlist?: boolean;
	duplicationGroup?: ApiPropertyDuplicationGroup;
	videoView?: VideoViewInfo;
}

/**
 * Maps a list of feature strings to an array of `FeatureObject`.
 *
 * @param features - A list of feature strings from the API.
 * @param enums - The system enums used to map features to categories.
 * @returns An array of `FeatureObject` containing the mapped feature data.
 */
export function mapFeatures(
	features: ApiProperty['features'],
	enums: SystemEnums
): FeatureObject[] {
	return (features ?? [])
		.map(feature => createFeatureObject(feature, enums))
		.filter(Boolean) as FeatureObject[];
}

/**
 * Creates a `FeatureObject` based on a feature string and the system enums.
 *
 * @param feature - A single feature string.
 * @param enums - The system enums used to retrieve the feature's category and name.
 * @returns A `FeatureObject` if the feature exists in the system enums, otherwise `null`.
 */
function createFeatureObject(
	feature: FeatureEnum | null,
	enums: SystemEnums
): FeatureObject | null {
	if (!feature) return null;

	const formattedFeature = feature?.toLowerCase().trim() as FeatureEnum;
	const featureObject = enums.features[formattedFeature];
	if (!featureObject) return null;

	return {
		name: featureObject.name,
		identifier: featureObject.identifier,
		category: featureObject.category
	};
}

/**
 * Groups features by their category names.
 *
 * @param features - An array of `FeatureObject`.
 * @returns An object where the keys are category names, and the values are arrays of `FeatureObject`.
 */
export function groupFeaturesByCategory(
	features: FeatureObject[]
): Record<string, FeatureObject[]> {
	return features.reduce(
		(acc, feature) => {
			const categoryName = featureCategoriesTranslations[feature.category];
			(acc[categoryName] ??= []).push(feature);
			return acc;
		},
		{} as Record<string, FeatureObject[]>
	);
}

/**
 * Maps the API property type to a domain-specific `PropertyTypeObject`.
 *
 * @param apiProperty - The property data from the API.
 * @param enums - The system enums used to map the property type.
 * @returns A `PropertyTypeObject` with the name and identifier of the property type.
 */
function mapApiPropertyType(
	apiProperty: ApiProperty,
	enums: SystemEnums
): PropertyTypeObject {
	const formattedPropertyType = apiProperty.propertyType
		?.toLowerCase()
		.trim() as PropertyTypeEnum;
	return {
		name: enums.propertyTypes[formattedPropertyType] || '',
		identifier: formattedPropertyType
	};
}

const slugPrefixes = Object.keys(propertyTypesValueBySlug).sort(
	(a, b) => b.length - a.length
);

function inferPropertyTypeFromSlug(
	slug: string
): { identifier: PropertyTypeEnum; name: string } | undefined {
	for (const prefix of slugPrefixes) {
		if (slug.startsWith(prefix + '-') || slug === prefix) {
			const identifier = propertyTypesValueBySlug[prefix as PropertyTypeSlug];
			return {
				identifier,
				name: propertyTypesTranslations[identifier] || ''
			};
		}
	}
	return undefined;
}

/**
 * Maps the API transaction type to a domain-specific `TransactionType`.
 *
 * @param transactionType - The transaction type from the API.
 * @returns A `TransactionType` based on the system mapping.
 */
function mapTransactionType(
	transactionType: ApiTransactionType
): TransactionType {
	return apiTransactionTypeToDomain[transactionType] || 'rentOrSell';
}

/**
 * Maps the API property images to domain-specific `PropertyImage` objects.
 *
 * @param images - An array of images from the API.
 * @param shouldShowClearImage - Should show clear image
 * @returns An array of `PropertyImage` with the `watermarkUrl` property.
 */
function mapImages(
	images: ApiPropertyImage[],
	{
		companyId,
		shouldShowClearImage = false
	}: { companyId: string; shouldShowClearImage: boolean }
): PropertyImage[] {
	return images
		.filter(image => shouldShowClearImage || image.portalWatermarkUrl?.trim())
		.map(image => ({
			watermarkUrl: image?.portalWatermarkUrl?.trim(),
			...(shouldShowClearImage ? { url: image?.url?.trim() } : {})
		}));
}
/**
 * Maps an `ApiAgent` profile picture to the `Agent` profile picture format.
 *
 * This function takes a profile picture from the `ApiAgent` format, which includes
 * an array of objects with `url` and `cropped_url`, and maps it to the `Agent` format,
 * which includes a single object with `url` and `croppedUrl`.
 *
 * If the provided `profilePicture` array is empty or null, a default object with
 * `null` values for `url` and `croppedUrl` is returned.
 *
 * @param profilePicture - The profile picture array from `ApiAgent`, containing objects
 * with `url` and `cropped_url` fields.
 * @returns A single `Agent`-formatted profile picture object with `url` and `croppedUrl`
 * fields, or a default object with null values if `profilePicture` is empty.
 */
function mapAgentProfilePicture(
	profilePicture: ApiAgent['profilePicture']
): Agent['profilePicture'] {
	const defaultAgentProfilePicture = {
		url: null,
		croppedUrl: null
	};

	if (!profilePicture?.length) return defaultAgentProfilePicture;

	return {
		url: profilePicture[0].url,
		croppedUrl: profilePicture[0].cropped_url
	};
}

function mapApiCluster(apiCluster?: ApiCluster): Cluster | undefined {
	if (!apiCluster) return undefined;

	return {
		...apiCluster,
		properties: snakeToCamelObject(apiCluster.properties)
	};
}

/**
 * Creates a domain-specific property object from the API property data.
 *
 * @param apiProperty - The property data from the API.
 * @param enums - The system enums used for mapping properties and features.
 * @param options - Additional options to map properties.
 * @returns A fully mapped `Property` object.
 */
export function createProperty(
	apiProperty: ApiProperty,
	enums: SystemEnums,
	options?: {
		shouldShowClearImage?: boolean;
	}
): Property {
	const features = mapFeatures(apiProperty.features, enums);
	const featuresByCategory = groupFeaturesByCategory(features);
	const propertyType = mapApiPropertyType(apiProperty, enums);
	const h3Cluster = mapApiCluster(apiProperty.h3Cluster);

	return {
		id: apiProperty._id,
		company: {
			id: apiProperty.companyId,
			name: apiProperty.company.name,
			smallLogo: apiProperty.company.smallLogo,
			largeLogo: apiProperty.company.largeLogo
		},
		slug: apiProperty.slug,
		agent: {
			id: apiProperty.agent._id,
			name: apiProperty.agent.name,
			profilePicture: mapAgentProfilePicture(apiProperty.agent.profilePicture),
			display_name: apiProperty.agent.displayName,
			slug: apiProperty.agent.slug ?? null
		},
		ad: {
			description: apiProperty.ad.description || '',
			title: apiProperty.ad.title || '',
			transactionType: mapTransactionType(apiProperty.ad.transactionType)
		},
		images: mapImages(apiProperty.images || [], {
			companyId: apiProperty.companyId,
			shouldShowClearImage: options?.shouldShowClearImage || false
		}),
		thumbnailUrl: resolveApiThumbnailUrl(apiProperty.thumbnail),
		askingPrice: apiProperty.askingPrice || null,
		rentPrice: apiProperty.rentPrice || null,
		isExclusive: apiProperty.isExclusive || false,
		area: apiProperty.area,
		totalArea: apiProperty.totalArea || null,
		bedrooms: apiProperty.bedrooms || null,
		bathrooms: apiProperty.bathrooms || null,
		suites: apiProperty.suites || null,
		parkingSpots: apiProperty.parkingSpots || null,
		condoFee: apiProperty.condoFee || null,
		building: apiProperty.building,
		tax: apiProperty.tax || null,
		propertyType,
		featuresByCategory,
		region: apiProperty.region,
		city: apiProperty.city,
		state: apiProperty.state,
		commercialId: apiProperty.commercialId,
		pluscodeGroup: apiProperty.pluscodeGroup,
		condoName: apiProperty.condoName,
		videos: normalizeApiYoutubeVideos(apiProperty.videos),
		featuredVideos: apiProperty.featuredVideos || [],
		launchDate: apiProperty.launchDate,
		publicationStatus: apiProperty.publicationStatus,
		canonicalCondo: apiProperty.canonicalCondo,
		poiDistanceTime: apiProperty.poiDistanceTime,
		h3Cluster,
		h3ClusterIds: apiProperty.h3ClusterIds,
		inUserWishlist: apiProperty.inUserWishlist,
		duplicationGroup: apiProperty.duplicationGroup,
		videoView: mapApiVideoView(apiProperty.videoView)
	};
}

/**
 * Creates a lightweight property object for shorts (short-form videos).
 * Uses only the fields available in the ShortVideoPropertyModel.
 *
 * @param apiProperty - The property data from the API.
 * @param enums - Optional system enums for mapping property type. When omitted, uses raw identifier/name.
 */
export function createShortVideoProperty(
	apiProperty: ApiProperty,
	enums?: SystemEnums
): ShortVideoProperty {
	return {
		id: apiProperty._id,
		slug: apiProperty.slug,
		commercialId: apiProperty.commercialId,
		ad: { title: apiProperty.ad?.title || '' },
		thumbnailUrl: resolveApiThumbnailUrl(apiProperty.thumbnail),
		askingPrice: apiProperty.askingPrice || null,
		area: apiProperty.area || 0,
		bedrooms: apiProperty.bedrooms || null,
		suites: apiProperty.suites || null,
		parkingSpots: apiProperty.parkingSpots || null,
		region: apiProperty.region || '',
		city: apiProperty.city || '',
		state: apiProperty.state || '',
		featuredVideos: apiProperty.featuredVideos ?? [],
		images: mapImages(apiProperty.images || [], {
			companyId: apiProperty.companyId ?? '',
			shouldShowClearImage: false
		}),
		videoView: mapApiVideoView(apiProperty.videoView),
		watched: apiProperty.watched,
		propertyType: apiProperty.propertyType
			? enums
				? mapApiPropertyType(apiProperty, enums)
				: (() => {
						const identifier = apiProperty.propertyType
							.toLowerCase()
							.trim() as PropertyTypeEnum;
						return {
							identifier,
							name: propertyTypesTranslations[identifier] || ''
						};
					})()
			: inferPropertyTypeFromSlug(apiProperty.slug || ''),
		agent: apiProperty.agent
			? {
					id: apiProperty.agent._id,
					name: apiProperty.agent.name || '',
					displayName: apiProperty.agent.displayName ?? null,
					profilePictureUrl: mapAgentProfilePicture(
						apiProperty.agent.profilePicture
					).croppedUrl,
					slug: apiProperty.agent.slug ?? null
				}
			: undefined,
		company: apiProperty.company
			? {
					name: apiProperty.company.name || '',
					logoUrl: apiProperty.company.smallLogo || null
				}
			: undefined,
		isExclusive: apiProperty.isExclusive || false
	};
}
